/**
 * ③監査ログ検索（08 §5.1・FR-71）＝運営が「誰がいつ何を」したかを追える。
 *
 * ops_audit_logs（テナント管理の操作）を action・対象テナントで絞り、(at,id) DESC の
 * keyset ページングで「さらに読み込む」（規約10＝OFFSET 禁止）。
 */

import { ops, type OpsAuditLog } from "@kenzai/api-client";
import { Badge, Card, Empty, ErrorNote, formatDateTime, PageHeader, Skeleton, Table, Td } from "@kenzai/ui";
import { useEffect, useState } from "react";

/** action の表示語彙（未知の値はそのまま出す＝新しい操作が増えても壊れない）。 */
const ACTION_LABEL: Record<string, string> = {
  "ops.tenant.open": "テナント開設",
  "ops.tenant.activate": "稼働（試用→稼働）",
  "ops.tenant.suspend": "停止",
  "ops.tenant.resume": "再開",
  "ops.tenant.reset_link": "パスワード設定URLの再発行",
};
const ACTION_CHOICES = Object.keys(ACTION_LABEL);

type Filters = { action: string; targetId: string };
// ★cursor を生んだ「適用済みフィルタ」を loaded に閉じ込める＝「さらに読み込む」は
// ドロップダウンの現在値でなく検索した時のフィルタで続きを引く（検索せずに絞りを変えても混ざらない）。
type Loaded = { items: OpsAuditLog[]; next: string | null; applied: Filters };

export function Audit() {
  const [action, setAction] = useState<string>("");
  const [targetId, setTargetId] = useState<string>("");
  const [loaded, setLoaded] = useState<Loaded>({
    items: [],
    next: null,
    applied: { action: "", targetId: "" },
  });
  const [phase, setPhase] = useState<"idle" | "loading" | "more" | "error">("loading");
  const [error, setError] = useState<unknown>(null);

  async function run(cursor: string | null, filters: Filters) {
    const append = cursor !== null;
    setPhase(append ? "more" : "loading");
    setError(null);
    try {
      const r = await ops.auditSearch({
        action: filters.action || undefined,
        target_id: filters.targetId.trim() || undefined,
        cursor: cursor ?? undefined,
      });
      setLoaded((prev) => ({
        items: append ? [...prev.items, ...r.items] : r.items,
        next: r.next_cursor,
        applied: filters,
      }));
      setPhase("idle");
    } catch (e) {
      setError(e);
      setPhase("error");
    }
  }

  // 初回のみ自動検索。以降はフィルタ変更→「検索」ボタンで明示的に引く。
  useEffect(() => {
    void run(null, { action: "", targetId: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    void run(null, { action, targetId });
  }

  return (
    <>
      <PageHeader title="監査ログ" subtitle="運営がテナントに対して行った操作の記録です。" />

      <Card style={{ marginBottom: "var(--kz-space-4)" }}>
        <form onSubmit={onSearch} style={filterRow}>
          <label style={fieldLabel}>
            操作
            <select
              data-testid="audit-action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              style={control}
            >
              <option value="">すべて</option>
              {ACTION_CHOICES.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
          <label style={fieldLabel}>
            テナントID
            <input
              data-testid="audit-target"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              inputMode="numeric"
              placeholder="例：12"
              style={control}
            />
          </label>
          <button type="submit" data-testid="audit-search" className="kz-btn kz-btn--primary">
            検索
          </button>
        </form>
      </Card>

      {phase === "loading" ? (
        // ロード中に `<table>` を置かない（素の帯だけ）＝E2E が「表＝データ確定」を前提にしている
        <Card>
          <Skeleton rows={6} height={18} />
        </Card>
      ) : phase === "error" ? (
        <ErrorNote error={error} />
      ) : loaded.items.length === 0 ? (
        <Empty title="記録がありません" hint="条件を変えて検索してください。" />
      ) : (
        <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
          <Card padding="0">
            <Table head={["日時", "操作", "対象", "実行者", "理由"]}>
              {loaded.items.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    <span className="kz-num">{formatDateTime(r.at)}</span>
                  </Td>
                  <Td>
                    <Badge tone="primary">{ACTION_LABEL[r.action] ?? r.action}</Badge>
                  </Td>
                  <Td style={{ whiteSpace: "nowrap" }}>
                    {r.target_type === "tenant" && r.target_id ? (
                      <span className="kz-num">テナント #{r.target_id}</span>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>{r.actor_email ?? "—"}</Td>
                  <Td>{r.reason ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          </Card>
          {loaded.next && (
            <button
              type="button"
              data-testid="audit-more"
              disabled={phase === "more"}
              onClick={() => void run(loaded.next, loaded.applied)}
              className="kz-btn"
              style={{ justifySelf: "center" }}
            >
              {phase === "more" ? "読み込み中…" : "さらに読み込む"}
            </button>
          )}
        </div>
      )}
    </>
  );
}

const filterRow: React.CSSProperties = {
  display: "flex",
  gap: "var(--kz-space-4)",
  alignItems: "flex-end",
  flexWrap: "wrap",
};
const fieldLabel: React.CSSProperties = {
  display: "grid",
  gap: "var(--kz-space-1)",
  fontSize: "var(--kz-font-size-sm)",
  color: "var(--kz-color-ink-muted)",
};
const control: React.CSSProperties = {
  font: "inherit",
  padding: "var(--kz-space-2) var(--kz-space-3)",
  border: "1px solid var(--kz-color-border)",
  borderRadius: "var(--kz-radius-sm)",
  background: "var(--kz-color-surface)",
  color: "var(--kz-color-ink)",
  minWidth: 180,
};
